import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddLinkOutlined from '@mui/icons-material/AddLinkOutlined';
import toast from 'react-hot-toast';
import { Button } from '@/ui';
import DropdownAutocomplete from '../../components/FormFields/DropdownAutocomplete';
import { buildTestLink, ENTITY_PARAM_ORDER, type CustomParam, type EntityParamKey } from './buildTestLink';
import {
  ENTITY_SITE_SCOPED,
  forGroup,
  forSite,
  siteMismatch,
  siteOfLandingPage,
  type DomainOption,
  type PickerOption,
} from './linkBuilderOptions';
import { useLinkBuilderOptions } from './useLinkBuilderOptions';
import { loadRecent, pruneStale, rememberLink, type BuilderState } from './storage';
import { copyText } from './clipboard';
import LinkOutput from './components/LinkOutput';
import EntityParamRow from './components/EntityParamRow';
import CustomParamRow from './components/CustomParamRow';
import RecentLinks from './components/RecentLinks';

const ENTITY_LABELS: Record<EntityParamKey, string> = {
  lpid: 'Landing page',
  lpgid: 'Landing page group',
  sequence_id: 'Sequence',
  offer_id: 'Offer',
};

const QUICK_PARAMS: CustomParam[] = [
  { key: 'cash', value: '10,000' },
  { key: 'email', value: 'test@testington.com' },
];

const FieldLabel: React.FC<{ htmlFor: string; children: React.ReactNode }> = ({ htmlFor, children }) => (
  <Typography component='label' htmlFor={htmlFor} variant='subtitle2' sx={{ display: 'block', mb: 0.5 }}>
    {children}
  </Typography>
);

export interface TestLinkBuilderPanelProps {
  initialState: BuilderState;
  /** Called with every change; the page uses it to remember the form in this browser. */
  onStateChange?: (state: BuilderState) => void;
  /** `dialog`: no heading card, and the link pins to the top of the dialog rather than under the app header. */
  variant?: 'page' | 'dialog';
  /**
   * Fill an empty site from the prefilled landing page group or sequence once the lists load, so a
   * link opened from that entity's screen targets its site.
   */
  inferSite?: boolean;
}

/** The builder itself: the form, the link it makes, and the recent links. The page and the dialog both render it. */
const TestLinkBuilderPanel: React.FC<TestLinkBuilderPanelProps> = ({
  initialState,
  onStateChange,
  variant = 'page',
  inferSite = false,
}) => {
  const lists = useLinkBuilderOptions();
  const [state, setState] = useState<BuilderState>(initialState);
  const [recent, setRecent] = useState<string[]>(loadRecent);

  const update = (patch: Partial<BuilderState>) => setState((prev) => ({ ...prev, ...patch }));

  // Saved ids for entities deleted since last visit would otherwise build a dead link.
  useEffect(() => {
    setState((prev) => pruneStale(prev, lists));
  }, [lists]);

  useEffect(() => {
    onStateChange?.(state);
  }, [state, onStateChange]);

  // Once, as soon as the lists it needs have loaded: clearing the site afterwards sticks.
  const siteInferred = useRef(false);
  useEffect(() => {
    if (!inferSite || siteInferred.current) return;
    if (lists.entities.lpgid.loading || lists.entities.sequence_id.loading) return;
    siteInferred.current = true;
    setState((prev) => {
      if (prev.siteId != null) return prev;
      const owner = (['lpgid', 'sequence_id'] as const)
        .map((key) => lists.entities[key].options.find((option) => option.id === prev.entities[key]))
        .find((option) => option?.siteId != null);
      const siteId = owner?.siteId ?? siteOfLandingPage(prev.entities.lpid, lists.entities.lpgid.options);
      return siteId != null ? { ...prev, siteId } : prev;
    });
  }, [inferSite, lists]);

  // A site with a single active domain gets it filled in. Once per site: clearing the domain
  // afterwards leaves it empty rather than putting it straight back.
  const autoPickedFor = useRef<number | null>(null);
  useEffect(() => {
    const { siteId, domainId } = state;
    if (siteId == null || autoPickedFor.current === siteId) return;
    // A site that already has its domain counts as done, so clearing that domain later sticks.
    if (domainId != null) {
      autoPickedFor.current = siteId;
      return;
    }
    if (lists.domains.loading || lists.domains.error) return;
    autoPickedFor.current = siteId;
    const candidates = lists.domains.options.filter((option) => option.siteId === siteId && !option.inactive);
    if (candidates.length === 1)
      setState((prev) => (prev.domainId == null ? { ...prev, domainId: candidates[0]!.id } : prev));
  }, [state.siteId, state.domainId, lists.domains]); // eslint-disable-line react-hooks/exhaustive-deps

  const domain = lists.domains.options.find((d) => d.id === state.domainId);
  const domainOptions = forSite(lists.domains.options, state.siteId, state.domainId, state.showAllDomains);

  const link = useMemo(
    () =>
      buildTestLink({
        domain: domain?.name ?? null,
        path: state.path,
        siteId: state.siteId,
        entities: state.entities,
        custom: state.custom,
      }),
    [domain?.name, state.path, state.siteId, state.entities, state.custom]
  );

  const pickDomain = (option: DomainOption | null) =>
    option
      ? update({ domainId: option.id, ...(option.siteId != null && { siteId: option.siteId }) })
      : update({ domainId: null });

  // A cleared picker keeps its row, empty, so the param can be picked again without re-adding it.
  const setEntity = (key: EntityParamKey, id: number | null) =>
    setState((prev) => ({ ...prev, entities: { ...prev.entities, [key]: id } }));

  const removeEntity = (key: EntityParamKey) =>
    setState((prev) => {
      const entities = { ...prev.entities };
      delete entities[key];
      return { ...prev, entities };
    });

  const setCustom = (index: number, param: CustomParam) =>
    setState((prev) => ({ ...prev, custom: prev.custom.map((p, i) => (i === index ? param : p)) }));

  const removeCustom = (index: number) =>
    setState((prev) => ({ ...prev, custom: prev.custom.filter((_, i) => i !== index) }));

  // Quick-adds are one-per-key; "+ Custom" (blank key) always adds a row.
  const addCustom = (param: CustomParam) =>
    setState((prev) =>
      param.key && prev.custom.some((p) => p.key === param.key) ? prev : { ...prev, custom: [...prev.custom, param] }
    );

  const copy = async (url: string) => {
    if (await copyText(url)) toast.success('Link copied');
    else toast.error('Copy failed — select the link and copy it manually.');
    setRecent((prev) => rememberLink(url, prev));
  };

  const open = () => {
    if (!link) return;
    window.open(link.url, '_blank', 'noopener');
    setRecent((prev) => rememberLink(link.url, prev));
  };

  const presentEntities = ENTITY_PARAM_ORDER.filter((key) => key in state.entities);
  const missingEntities = ENTITY_PARAM_ORDER.filter((key) => !(key in state.entities));
  const hasLandingTarget = state.entities.lpid != null || state.entities.lpgid != null;

  const entityHint = (key: EntityParamKey): string | undefined => {
    if (key === 'lpgid' && state.entities.lpid != null) return 'Ignored while a landing page is set — lpid wins.';
    if (key === 'offer_id') return "Shows this offer directly; posts don't fire for it.";
    return undefined;
  };

  const selectedGroup = lists.entities.lpgid.options.find((option) => option.id === state.entities.lpgid);
  const groupNarrows = selectedGroup?.landingPageIds != null;

  return (
    <Stack spacing={2}>
      <LinkOutput
        link={link}
        onCopy={() => link && copy(link.url)}
        onOpen={open}
        pinTop={variant === 'dialog' ? 0 : undefined}
      />

      <Card>
        {variant === 'page' && (
          <CardHeader
            avatar={<AddLinkOutlined color='action' />}
            title='Test Link Builder'
            subheader='Builds a link that starts a fresh SPA session for testing.'
          />
        )}
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FieldLabel htmlFor='tlb-site'>Site</FieldLabel>
              <DropdownAutocomplete
                id='tlb-site'
                options={lists.sites.options}
                value={state.siteId}
                valueField='id'
                textField='label'
                busy={lists.sites.loading}
                error={lists.sites.error}
                placeholder='Select a site…'
                clearable
                onChange={(option: PickerOption | null) => update({ siteId: option?.id ?? null })}
              />
              <FormHelperText error={lists.sites.error}>
                {lists.sites.error
                  ? "Couldn't load sites."
                  : 'Set from the domain; change it to test on another site (e.g. 75).'}
              </FormHelperText>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FieldLabel htmlFor='tlb-domain'>Domain</FieldLabel>
              <DropdownAutocomplete
                id='tlb-domain'
                options={domainOptions}
                value={state.domainId}
                valueField='id'
                textField='label'
                busy={lists.domains.loading}
                error={lists.domains.error}
                placeholder='Select a domain…'
                clearable
                onChange={pickDomain}
              />
              {lists.domains.error && <FormHelperText error>Couldn&apos;t load domains.</FormHelperText>}
              {state.siteId != null && (
                <FormControlLabel
                  control={
                    <Switch
                      size='small'
                      checked={state.showAllDomains}
                      onChange={(e) => update({ showAllDomains: e.target.checked })}
                    />
                  }
                  label='Show all domains'
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FieldLabel htmlFor='tlb-path'>Path</FieldLabel>
              <TextField
                id='tlb-path'
                size='small'
                fullWidth
                value={state.path}
                onChange={(e) => update({ path: e.target.value })}
                helperText='/api/user redirects to / with the same params.'
                slotProps={{ htmlInput: { spellCheck: false } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip label='forceReload=true · always added' variant='outlined' color='warning' />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title='Parameters' />
        <CardContent>
          <Stack spacing={2}>
            {presentEntities.map((key) => {
              const list = lists.entities[key];
              const value = state.entities[key] ?? null;
              const scoped = ENTITY_SITE_SCOPED[key];
              const selected = list.options.find((o) => o.id === value);
              const options =
                key === 'lpid'
                  ? forGroup(list.options, selectedGroup, value, state.showAllLandingPages)
                  : scoped
                    ? forSite(list.options, state.siteId, value)
                    : list.options;
              return (
                <EntityParamRow
                  key={key}
                  id={`tlb-${key}`}
                  label={ENTITY_LABELS[key]}
                  paramKey={key}
                  options={options}
                  value={value}
                  loading={list.loading}
                  error={list.error}
                  hint={entityHint(key)}
                  warning={
                    scoped && siteMismatch(selected, state.siteId)
                      ? `Belongs to site #${selected?.siteId}, not #${state.siteId}.`
                      : undefined
                  }
                  onChange={(id) => setEntity(key, id)}
                  onRemove={() => removeEntity(key)}
                  extra={
                    key === 'lpid' && groupNarrows ? (
                      <FormControlLabel
                        control={
                          <Switch
                            size='small'
                            checked={state.showAllLandingPages}
                            onChange={(e) => update({ showAllLandingPages: e.target.checked })}
                          />
                        }
                        label={
                          state.showAllLandingPages
                            ? 'Showing every landing page'
                            : `Show all landing pages (listing the ${selectedGroup?.landingPageIds?.length ?? 0} in group #${selectedGroup?.id})`
                        }
                      />
                    ) : undefined
                  }
                />
              );
            })}

            {state.custom.map((param, index) => (
              <CustomParamRow
                key={index}
                index={index}
                param={param}
                ignored={!!link?.ignoredCustom.includes(index)}
                onChange={(next) => setCustom(index, next)}
                onRemove={() => removeCustom(index)}
              />
            ))}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {missingEntities.map((key) => (
                <Button key={key} size='sm' color='primary' variant='outlined' onClick={() => setEntity(key, null)}>
                  + {ENTITY_LABELS[key]}
                </Button>
              ))}
              {QUICK_PARAMS.map((param) => (
                <Button key={param.key} size='sm' color='primary' variant='outlined' onClick={() => addCustom(param)}>
                  + {param.key}
                </Button>
              ))}
              <Button size='sm' color='primary' variant='outlined' onClick={() => addCustom({ key: '', value: '' })}>
                + Custom
              </Button>
            </Box>
            {!hasLandingTarget && (
              <FormHelperText>No landing page or group set — the domain&apos;s default group is used.</FormHelperText>
            )}
          </Stack>
        </CardContent>
      </Card>

      <RecentLinks links={recent} onCopy={copy} />
    </Stack>
  );
};

export default TestLinkBuilderPanel;
